import { createModel } from '@rematch/core';
import createDataAccessAxiosInstance from './createDataAccessAxiosInstance';
import RepMfrDataAccessStatus from '../../RepMfrDataAccessStatuses';

export type ManufacturerItem = {
  manufacturerId: string;
  manufacturerName: string;
  status: string;
};

export type ShareDataManufacturersState = {
  manufacturers: ManufacturerItem[];
  selectedManufacturers: ManufacturerItem[];
  legalTextDisplayed: boolean;
  consentAccepted: boolean;
  isMfrChecked: boolean;
  pendingApprovalsCount: number;
};

const defaultState: ShareDataManufacturersState = {
  manufacturers: [],
  selectedManufacturers: [],
  legalTextDisplayed: false,
  consentAccepted: false,
  isMfrChecked: false,
  pendingApprovalsCount: 0
};

const mapMfrsByStatus = function mapMfrsByStatus(
  source: ManufacturerItem[],
  statuses: (string | null)[]
): { id: string; text: string; preselected: boolean }[] {
  return source
    .filter(r => statuses.includes(r.status))
    .map(m => ({
      id: m.manufacturerId,
      text: m.manufacturerName,
      preselected: false
    }));
};

const setManufacturers = function handleSetReps(
  state: ShareDataManufacturersState,
  manufacturers: []
): ShareDataManufacturersState {
  const pendingApprovals = mapMfrsByStatus(manufacturers, [
    RepMfrDataAccessStatus.Pending
  ]).length;
  return {
    ...state,
    manufacturers,
    pendingApprovalsCount: pendingApprovals
  };
};

const setLegalTextDisplay = function handleSetLegalTextDisplay(
  state: ShareDataManufacturersState,
  legalTextDisplayed: boolean
): ShareDataManufacturersState {
  return {
    ...state,
    legalTextDisplayed
  };
};

const toggleLegalTextDisplay = function handleToggleLegalTextDisplay(
  state: ShareDataManufacturersState
): ShareDataManufacturersState {
  return {
    ...state,
    legalTextDisplayed: !state.legalTextDisplayed
  };
};

const toggleConsentAcceptance = function handleToggleConsentAcceptance(
  state: ShareDataManufacturersState
): ShareDataManufacturersState {
  return {
    ...state,
    consentAccepted: !state.consentAccepted
  };
};

const checkConsentAcceptance = function handleCheckConsentAcceptance(
  state: ShareDataManufacturersState,
  accepted: boolean
): ShareDataManufacturersState {
  return {
    ...state,
    consentAccepted: accepted
  };
};

const setSelectedManufacturers = function handleSetSelectedManufacturers(
  state: ShareDataManufacturersState,
  selectedManufacturers: []
): ShareDataManufacturersState {
  return {
    ...state,
    selectedManufacturers
  };
};

const setIsMfrChecked = function handleIsMfrChecked(
  state: ShareDataManufacturersState,
  isMfrChecked: boolean
): ShareDataManufacturersState {
  return {
    ...state,
    isMfrChecked
  };
};

const shareDataManufacturers = createModel({
  state: defaultState,
  reducers: {
    setManufacturers,
    setSelectedManufacturers,
    setLegalTextDisplay,
    toggleLegalTextDisplay,
    toggleConsentAcceptance,
    checkConsentAcceptance,
    setIsMfrChecked
  },
  effects: {
    async getAvailableRepManufacturers(payload): Promise<void> {
      const { companyId, dataAccessToken } = payload;
      const axiosApiInstance = createDataAccessAxiosInstance(dataAccessToken);
      const repManufacturersCall = axiosApiInstance.get(
        `/api/insight/rep/${companyId}/manufacturers`
      );
      const { data: manufacturersData } = await repManufacturersCall;
      this.setManufacturers(manufacturersData);
    },
    async shareData(payload): Promise<void> {
      const { mfrStatusList, dataAccessToken } = payload;
      const axiosApiInstance = createDataAccessAxiosInstance(dataAccessToken);
      await axiosApiInstance.post(
        `api/insight/rep/savemfrstatus`,
        mfrStatusList
      );
    }
  }
});

export default shareDataManufacturers;
